import React, { useState, useEffect, useCallback } from "react";
import {
  ShieldAlert,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowRight,
  Loader2,
  XCircle,
  Mail
} from "lucide-react";
import { Link } from "react-router-dom";
import { API_BASE } from "../config/api";
import { useAuth } from "./AuthContext";
import { formatAuthErrorMessage } from "./authErrorUtils";
import { GoogleAuthIcon } from "../components/ui/GoogleAuthIcon";

interface Reset2FaPageProps {
  onGoLogin: () => void;
  token?: string;
}

export function Reset2FaPage({ onGoLogin, token: propToken }: Reset2FaPageProps) {
  const token = propToken || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('token') : null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { clearError } = useAuth();

  useEffect(() => {
    window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    clearError();
    setError(null);
    return () => {
      clearError();
      setError(null);
    };
  }, [clearError]);

  const handleGoLogin = () => {
    clearError();
    setError(null);
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', window.location.pathname);
    }
    onGoLogin();
  };
  
  const [status, setStatus] = useState<'pending_confirmation' | 'cooling_off' | 'completed' | 'cancelled' | 'expired' | 'invalid'>('pending_confirmation');
  const [maskedEmail, setMaskedEmail] = useState<string>('');
  const [unlocksAt, setUnlocksAt] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number } | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!token) {
      setStatus('invalid');
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/auth/2fa/reset-status?token=${encodeURIComponent(token)}`);
      const data = await res.json();

      if (res.status === 410 || data.status === 'expired') {
        setStatus('expired');
        setError(data.error || 'Ce lien a expiré.');
        return;
      }

      if (!res.ok || !data.success) {
        setStatus('invalid');
        setError(data.error || 'Lien invalide ou introuvable.');
        return;
      }

      setStatus(data.status);
      setMaskedEmail(data.email || '');
      setUnlocksAt(data.unlocksAt || null);
    } catch (err: any) {
      setError(err.message || 'Erreur de connexion.');
      setStatus('invalid');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Calcul du compte à rebours 24h
  useEffect(() => {
    if (status !== 'cooling_off' || !unlocksAt) return;

    const updateTimer = () => {
      const diff = new Date(unlocksAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft(null);
        setStatus('completed');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [status, unlocksAt]);

  const handleAction = async (action: 'approve' | 'cancel') => {
    if (!token) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/auth/2fa/confirm-reset-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, action })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Action impossible');
      }

      setStatus(data.status);
      if (data.unlocksAt) {
        setUnlocksAt(data.unlocksAt);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la validation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] w-full flex flex-col justify-center items-center bg-white px-4 sm:px-8 py-6 sm:py-10">
      <div className="w-full max-w-[440px] my-auto space-y-4 py-2">

        {/* Logo */}
        <div className="text-center flex flex-col items-center">
          <Link to="/" className="inline-block cursor-pointer transition-transform hover:scale-[1.02]">
            <img src="/logo.webp" alt="Zeyilo" className="h-10 sm:h-11 w-auto object-contain mb-1.5" />
          </Link>
          <p className="text-slate-500 text-xs">Centre de Sécurité & Protection de Compte</p>
        </div>

        {/* Card principale Bento */}
        <div className="bg-slate-100/80 rounded-2xl p-3.5 sm:p-5 border-0 shadow-none space-y-3 sm:space-y-4 animate-in fade-in zoom-in-95 duration-200">
          
          {isLoading ? (
            <div className="py-12 bg-white rounded-xl flex flex-col items-center justify-center gap-3 text-slate-500 border-0 shadow-none">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              <p className="text-xs font-medium">Vérification du lien de sécurité...</p>
            </div>
          ) : status === 'pending_confirmation' ? (
            /* STEP 1: Confirmation « Oui c'est moi » vs « Non annuler » */
            <div className="space-y-3 sm:space-y-3.5">
              {/* Header Bento */}
              <div className="bg-white rounded-xl p-3.5 sm:p-4 border-0 shadow-none flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                  <ShieldAlert className="w-5 h-5 stroke-[2.2]" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                    Demande de Réinitialisation 2FA
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                    Compte : <span className="font-semibold text-slate-800">{maskedEmail}</span>
                  </p>
                </div>
              </div>

              {/* Sous-Bento blanc : Explications & Règles de sécurité (sans surcharge) */}
              <div className="bg-white rounded-xl p-3.5 sm:p-4 border-0 shadow-none space-y-3">
                <div>
                  <h1 className="text-xs sm:text-sm font-bold text-slate-950 leading-snug">
                    Confirmez-vous cette demande de déblocage ?
                  </h1>
                  <p className="text-slate-500 text-[11px] mt-1 leading-relaxed">
                    Pour garantir l'intégrité de vos données, un délai de sécurité de <strong className="text-slate-900">24 heures</strong> sera déclenché avant la suppression du double facteur.
                  </p>
                </div>

                {error && (
                  <div className="p-2.5 bg-rose-50 text-rose-800 text-xs rounded-xl flex items-center gap-2 border-0">
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                    <span>{formatAuthErrorMessage(error, "Une erreur est survenue.")}</span>
                  </div>
                )}

                {/* Checklist des 3 règles de sécurité */}
                <div className="space-y-1.5 pt-1 border-t border-slate-100">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                    Garanties de protection :
                  </span>
                  <div className="space-y-1 text-[11px] text-slate-600">
                    <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg">
                      <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
                      <span>Délai de sécurité incompressible de <strong>24 heures</strong></span>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg">
                      <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
                      <span>Protection contre tout piratage ou usurpation</span>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg">
                      <span className="w-4 h-4 rounded-md bg-white text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0">3</span>
                      <span>Confirmation automatique envoyée par email</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => handleAction('approve')}
                    className="w-full py-2.5 sm:py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs border-0 active:scale-[0.99]"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Oui, c'est bien moi (Lancer le délai de 24h)</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => handleAction('cancel')}
                    className="w-full py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-rose-50 text-slate-700 hover:text-rose-700 font-semibold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer border-0 active:scale-[0.99]"
                  >
                    <XCircle className="w-4 h-4" />
                    <span>Non, ce n'est pas moi (Annuler et bloquer)</span>
                  </button>
                </div>
              </div>
            </div>
          ) : status === 'cooling_off' ? (
            /* STEP 2: Période de sécurité 24h en cours (Compte à rebours) */
            <div className="space-y-3 sm:space-y-3.5">
              <div className="bg-white rounded-xl p-4 sm:p-5 border-0 shadow-none text-center space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center mx-auto border-0">
                  <Clock className="w-6 h-6 animate-pulse" />
                </div>

                <div className="space-y-1">
                  <h1 className="text-xs sm:text-sm font-bold text-slate-950">Délai de sécurité en cours</h1>
                  <p className="text-[11px] text-slate-500 max-w-sm mx-auto">
                    Demande validée pour <strong className="text-slate-800">{maskedEmail}</strong>. Votre compte sera débloqué automatiquement à l'échéance.
                  </p>
                </div>

                {timeLeft && (
                  <div className="p-3.5 bg-slate-900 rounded-xl text-white space-y-1.5">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">
                      Temps restant avant déblocage :
                    </span>
                    <div className="flex items-center justify-center gap-2 sm:gap-3 font-mono text-xl sm:text-2xl font-bold text-sky-400">
                      <div className="flex flex-col items-center">
                        <span>{String(timeLeft.hours).padStart(2, '0')}</span>
                        <span className="text-[8px] text-slate-400 font-sans uppercase">heures</span>
                      </div>
                      <span>:</span>
                      <div className="flex flex-col items-center">
                        <span>{String(timeLeft.minutes).padStart(2, '0')}</span>
                        <span className="text-[8px] text-slate-400 font-sans uppercase">min</span>
                      </div>
                      <span>:</span>
                      <div className="flex flex-col items-center">
                        <span>{String(timeLeft.seconds).padStart(2, '0')}</span>
                        <span className="text-[8px] text-slate-400 font-sans uppercase">sec</span>
                      </div>
                    </div>
                  </div>
                )}

                {unlocksAt && (
                  <p className="text-[11px] text-slate-500 font-medium">
                    Déblocage prévu le : <strong className="text-slate-800">{new Date(unlocksAt).toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'short' })}</strong>
                  </p>
                )}

                <div className="p-2.5 bg-slate-50 rounded-xl text-xs text-slate-600 flex items-center gap-2 text-left border-0">
                  <Mail className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-[11px]">Un email de confirmation vous sera envoyé dès que le 2FA sera levé.</span>
                </div>

                <div className="pt-1">
                  <button
                    type="button"
                    onClick={handleGoLogin}
                    className="w-full py-2.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer border-0 active:scale-[0.99]"
                  >
                    <span>Retour à la page de connexion</span>
                  </button>
                </div>
              </div>
            </div>
          ) : status === 'completed' ? (
            /* STEP 3: 2FA Réinitialisé avec succès */
            <div className="space-y-3 sm:space-y-3.5">
              <div className="bg-white rounded-xl p-4 sm:p-5 border-0 shadow-none text-center space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto border-0">
                  <CheckCircle2 className="w-6 h-6" />
                </div>

                <div className="space-y-1">
                  <h1 className="text-xs sm:text-sm font-bold text-slate-950">2FA Réinitialisé avec succès !</h1>
                  <p className="text-[11px] text-slate-600 max-w-sm mx-auto">
                    Le délai de 24h est écoulé. La protection Google Authenticator a été levée pour <strong className="text-slate-800">{maskedEmail}</strong>.
                  </p>
                </div>

                <div className="p-3 bg-emerald-50 rounded-xl text-xs text-emerald-800 font-medium text-left border-0">
                  Vous pouvez désormais vous connecter directement avec votre mot de passe et reconfigurer votre 2FA.
                </div>

                <button
                  type="button"
                  onClick={handleGoLogin}
                  className="w-full py-2.5 sm:py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs border-0 active:scale-[0.99]"
                >
                  <span>Se connecter maintenant</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : status === 'cancelled' ? (
            /* Demande annulée */
            <div className="space-y-3 sm:space-y-3.5">
              <div className="bg-white rounded-xl p-4 sm:p-5 border-0 shadow-none text-center space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-700 flex items-center justify-center mx-auto border-0">
                  <ShieldCheck className="w-6 h-6 text-emerald-600" />
                </div>

                <div className="space-y-1">
                  <h1 className="text-xs sm:text-sm font-bold text-slate-950">Demande Annulée</h1>
                  <p className="text-[11px] text-slate-500 max-w-sm mx-auto">
                    La réinitialisation a été annulée. Votre compte reste sécurisé par Google Authenticator.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleGoLogin}
                  className="w-full py-2.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer border-0 active:scale-[0.99]"
                >
                  <span>Retour à la connexion</span>
                </button>
              </div>
            </div>
          ) : (
            /* Lien expiré ou invalide */
            <div className="space-y-3 sm:space-y-3.5">
              <div className="bg-white rounded-xl p-4 sm:p-5 border-0 shadow-none text-center space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto border-0">
                  <AlertCircle className="w-6 h-6" />
                </div>

                <div className="space-y-1">
                  <h1 className="text-xs sm:text-sm font-bold text-slate-950">Lien invalide ou expiré</h1>
                  <p className="text-[11px] text-slate-500 max-w-sm mx-auto">
                    {formatAuthErrorMessage(error, 'Ce lien de confirmation n\'est plus valide. Veuillez relancer une demande depuis la page de connexion.')}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleGoLogin}
                  className="w-full py-2.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer border-0 active:scale-[0.99]"
                >
                  <span>Retour à la connexion</span>
                </button>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
